import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Injector,
  input,
  model,
  signal,
} from '@angular/core';
import { DsTabComponent } from './tab/tab.component';
import { TABS_HOST, TabsHost } from './tabs-host';
import {
  TabItem,
  TabsColor,
  TabsLayout,
  TabsSize,
  TabsVariant,
} from './tabs.types';

@Component({
  selector: 'soft-ds-tabs',
  imports: [DsTabComponent],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TABS_HOST,
      useExisting: forwardRef(() => DsTabsComponent),
    },
  ],
  host: {
    role: 'tablist',
    '[class]': 'hostClasses()',
    '[attr.aria-orientation]': '"horizontal"',
  },
})
export class DsTabsComponent<T = unknown> implements TabsHost<T> {
  private readonly el = inject(ElementRef<HTMLElement>);

  readonly value = model<T | null>(null);
  readonly tabs = input<readonly TabItem<T>[] | null>(null);
  readonly size = input<TabsSize>('md');
  readonly color = input<TabsColor>('default');
  readonly variant = input<TabsVariant>('segmented');
  readonly layout = input<TabsLayout>('auto');

  protected readonly projectedTabs = contentChildren<DsTabComponent<T>>(
    DsTabComponent,
    { descendants: true }
  );

  protected readonly hostClasses = computed(
    () =>
      `soft-ds-tabs-host size-${this.size()} color-${this.color()} variant-${this.variant()} layout-${this.layout()}`
  );

  /* Sliding underline indicator (only rendered in the `underline` variant).
     Position/width are read from the active tab's DOM rect after each render.
     `indicatorReady` gates the CSS transition so the indicator doesn't slide
     in from 0,0 on first paint. */
  protected readonly indicatorLeft = signal('0px');
  protected readonly indicatorWidth = signal('0px');
  protected readonly indicatorReady = signal(false);

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    effect(() => {
      // Re-read on value / tabs / projected-tab / variant change.
      this.value();
      this.tabs();
      this.projectedTabs();
      this.variant();
      this.layout();
      afterNextRender(
        {
          read: () => {
            this.observeTabSizes();
            this.updateIndicator();
          },
        },
        { injector: this.injector },
      );
    });
    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  /* Tabs change size after the first measurement (badges/titles arriving async,
     font load, container resize), which shifts the active tab — re-measure. */
  private observeTabSizes(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    const v = this.variant();
    if (v !== 'underline' && v !== 'underline-bg') return;
    this.resizeObserver ??= new ResizeObserver(() => this.updateIndicator());
    const host = this.el.nativeElement as HTMLElement;
    this.resizeObserver.observe(host);
    host
      .querySelectorAll<HTMLElement>('soft-ds-tab')
      .forEach((tab) => this.resizeObserver?.observe(tab));
  }

  private updateIndicator(): void {
    const v = this.variant();
    if (v !== 'underline' && v !== 'underline-bg') return;
    const host = this.el.nativeElement as HTMLElement;
    const triggers = host.querySelectorAll<HTMLElement>('soft-ds-tab');
    const items = this.tabs();
    let activeIdx = -1;
    if (items) {
      activeIdx = items.findIndex((t) => t.value === this.value());
    } else {
      const projected = this.projectedTabs();
      activeIdx = projected.findIndex((t) => t.value() === this.value());
    }
    const activeEl = activeIdx >= 0 ? triggers[activeIdx] : null;
    if (!activeEl) {
      this.indicatorWidth.set('0px');
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    this.indicatorLeft.set(
      `${activeRect.left - hostRect.left + host.scrollLeft}px`,
    );
    this.indicatorWidth.set(`${activeRect.width}px`);
    if (!this.indicatorReady()) this.indicatorReady.set(true);
  }

  protected trackTab = (_: number, item: TabItem<T>): T => item.value;

  selectValue(value: T): void {
    this.value.set(value);
  }

  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    const items = this.tabs();
    const enabled: { value: T; disabled: boolean }[] = items
      ? items
          .filter((t) => !t.disabled)
          .map((t) => ({ value: t.value, disabled: false }))
      : this.projectedTabs()
          .filter((t) => !t.disabled())
          .map((t) => ({ value: t.value(), disabled: false }));

    if (!enabled.length) return;

    const currentIdx = enabled.findIndex((t) => t.value === this.value());
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIdx = ((currentIdx === -1 ? 0 : currentIdx) + 1) % enabled.length;
        break;
      case 'ArrowLeft':
        nextIdx =
          ((currentIdx === -1 ? 0 : currentIdx) - 1 + enabled.length) % enabled.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = enabled.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    queueMicrotask(() => this.focusTabByValue(enabled[nextIdx].value));
  }

  private focusTabByValue(value: T): void {
    const host = this.el.nativeElement as HTMLElement;
    const triggers = host.querySelectorAll<HTMLElement>('soft-ds-tab');
    const items = this.tabs();
    if (items) {
      const idx = items.findIndex((t) => t.value === value);
      triggers[idx]?.focus();
      return;
    }
    const projected = this.projectedTabs();
    const idx = projected.findIndex((t) => t.value() === value);
    triggers[idx]?.focus();
  }
}
